import type { Params } from '@vimcar/mvvm/linkViewModel'
import type { ViewState } from '#components/Root/viewModel'
import type { Transitioner } from '#components/TransitionProvider'

import * as mobx from 'mobx'
import PT from 'prop-types'

import type { Nodes } from '../../models/Nodes'

import {
  CUSTOM_WORKSHOP_TIME_INTERVAL,
  CUSTOM_TRIPS_TIME_INTERVAL,
  ATTRIBUTE_PRIORITY,
  ATTRIBUTE_STARTSERVTIMESTAMP,
  ATTRIBUTE_ENDSERVTIMESTAMP,
  ATTRIBUTE_TRIPSSTART,
  ATTRIBUTE_TRIPSEND,
  ATTRVALUE_PRIORITY_LOW,
  ATTRVALUE_PRIORITY_NORMAL,
  ATTRVALUE_PRIORITY_HIGH,
} from '../../utils/constants'

export type Props = {
  state: ViewState
}

export type ViewModel = {
  startTimestamp?: Date | null
  endTimestamp?: Date | null
  today?: Date | null
  isValid: boolean

  handleNext: () => void
  handleBack: () => void
  handleNowClick: () => void
  handleStartDateChange: (string) => void
  handleEndDateChange: (string) => void
}

type Context = {
  models: { nodes: Nodes }
  transitioner: Transitioner
}

const mkViewModel = (
  params: Params,
  { state }: Props,
  { transitioner }: Context,
): ViewModel => {
  const vm = mobx.observable({
    startTimestamp: null,
    endTimestamp: null,
    today: new Date(),

    get isValid() {
      return !!(vm.startTimestamp && vm.endTimestamp)
    },
    handleNext: mobx.action(() => {
      const today = new Date().getTime()
      const startDate = new Date(vm.startTimestamp).getTime()
      const endDate = new Date(vm.endTimestamp).getTime()
      if (state.currentNode.customType === CUSTOM_WORKSHOP_TIME_INTERVAL) {
        state.customAttributes.set(
          ATTRIBUTE_STARTSERVTIMESTAMP,
          vm.startTimestamp,
        )
        state.customAttributes.set(ATTRIBUTE_ENDSERVTIMESTAMP, vm.endTimestamp)
        const delta = Math.ceil((today - endDate) / (1000 * 3600 * 24))
        if (delta <= 3) {
          state.customAttributes.set(ATTRIBUTE_PRIORITY, ATTRVALUE_PRIORITY_LOW)
        } else if (delta <= 6) {
          state.customAttributes.set(
            ATTRIBUTE_PRIORITY,
            ATTRVALUE_PRIORITY_NORMAL,
          )
        } else if (delta <= 8) {
          state.customAttributes.set(
            ATTRIBUTE_PRIORITY,
            ATTRVALUE_PRIORITY_HIGH,
          )
        } else {
          state.customAttributes.set(ATTRIBUTE_PRIORITY, ATTRVALUE_PRIORITY_LOW)
        }
      } else if (state.currentNode.customType === CUSTOM_TRIPS_TIME_INTERVAL) {
        state.customAttributes.set(ATTRIBUTE_TRIPSSTART, vm.startTimestamp)
        state.customAttributes.set(ATTRIBUTE_TRIPSEND, vm.endTimestamp)
        const deltaStart = Math.ceil((today - startDate) / (1000 * 3600 * 24))
        const deltaEnd = Math.ceil((today - endDate) / (1000 * 3600 * 24))
        if (deltaEnd > 9) {
          state.customAttributes.set(ATTRIBUTE_PRIORITY, ATTRVALUE_PRIORITY_LOW)
        } else if (deltaStart > 6) {
          state.customAttributes.set(
            ATTRIBUTE_PRIORITY,
            ATTRVALUE_PRIORITY_HIGH,
          )
        } else if (deltaStart > 3) {
          state.customAttributes.set(
            ATTRIBUTE_PRIORITY,
            ATTRVALUE_PRIORITY_NORMAL,
          )
        } else if (deltaStart <= 3) {
          state.customAttributes.set(ATTRIBUTE_PRIORITY, ATTRVALUE_PRIORITY_LOW)
        }
      }
      transitioner.goForward(
        state.currentNode.nextNode && state.currentNode.nextNode.id,
      )
    }),
    handleBack: mobx.action(() => {
      if (state.currentNode.customType === CUSTOM_WORKSHOP_TIME_INTERVAL) {
        state.customAttributes.delete(ATTRIBUTE_STARTSERVTIMESTAMP)
        state.customAttributes.delete(ATTRIBUTE_ENDSERVTIMESTAMP)
      } else if (state.currentNode.customType === CUSTOM_TRIPS_TIME_INTERVAL) {
        state.customAttributes.delete(ATTRIBUTE_TRIPSSTART)
        state.customAttributes.delete(ATTRIBUTE_TRIPSEND)
      }
    }),
    handleStartDateChange: mobx.action((value) => {
      // @ts-expect-error@2769
      vm.startTimestamp = new Date(value)
    }),
    handleEndDateChange: mobx.action((value) => {
      // @ts-expect-error@2769
      vm.endTimestamp = new Date(value)
    }),
    handleNowClick: mobx.action(() => {
      vm.endTimestamp = new Date()
    }),
  })

  return vm
}

mkViewModel.contextTypes = {
  transitioner: PT.object.isRequired,
}

export default mkViewModel
